// FWAA Business Exchange — attendee detail (regional or PM).
// Their portfolio with the canonical 4 columns, contact info, and the
// claim button front-and-center.

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { ClaimControl, ClaimSummary, CompleteToggle, FwaaPropertyTable, NotesThread, RoleBadge } from '../components/FwaaShared';

export default function FwaaAttendeePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    api.getFwaaAttendee(id)
      .then(d => { setData(d); setError(''); })
      .catch(err => setError(err?.responseBody?.error || err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="directory-page fwaa-page"><p>Loading...</p></div>;
  if (error || !data) {
    return (
      <div className="directory-page fwaa-page">
        <Link to="/sales/fwaa" className="fwaa-back">← FWAA Business Exchange</Link>
        <div className="card" style={{ padding: 16, color: '#dc2626' }}>{error || 'Not found'}</div>
      </div>
    );
  }

  const { attendee, company, properties, contact, claim_board } = data;
  const apolloCount = properties.filter(p => p.apollo_customer).length;

  return (
    <div className="directory-page fwaa-page">
      <Link to={`/sales/fwaa/companies/${company.id}`} className="fwaa-back">← {company.name}</Link>

      <div className="fwaa-detail-head">
        <div>
          <h1 className="fwaa-hero__title">
            {attendee.name} <RoleBadge roleType={attendee.role_type} title={attendee.match_result} />
          </h1>
          <p className="fwaa-hero__sub">
            {attendee.title ? `${attendee.title} · ` : ''}{company.name}
            {attendee.matched_aln_name && attendee.matched_aln_name !== attendee.name
              ? ` · in ALN as "${attendee.matched_aln_name}"` : ''}
          </p>
          {(contact.email || contact.phone) ? (
            <p className="fwaa-contact">
              {contact.email ? <a href={`mailto:${contact.email}`}>{contact.email}</a> : null}
              {contact.email && contact.phone ? ' · ' : ''}
              {contact.phone ? <a href={`tel:${contact.phone}`}>{contact.phone}</a> : null}
            </p>
          ) : null}
        </div>
        <div className="fwaa-detail-head__right">
          <div className="fwaa-detail-head__claimrow">
            <ClaimControl attendee={attendee} myUserId={user?.id} onChanged={load} size="lg" />
            {attendee.claimed_by === user?.id ? (
              <CompleteToggle attendeeId={attendee.id} status={attendee.claim_status} onChanged={load} />
            ) : null}
          </div>
          <ClaimSummary board={claim_board} />
        </div>
      </div>

      <div className="fwaa-kpi-row">
        <HeadKpi value={properties.length} label="Their Properties" />
        <HeadKpi value={apolloCount} label="Apollo Accounts" accent="green" />
        <HeadKpi value={properties.length - apolloCount} label="Open Opportunities" accent={properties.length - apolloCount > 0 ? 'amber' : undefined} />
      </div>

      {apolloCount > 0 ? (
        <p className="fwaa-pitch-tip">
          <strong>Warm intro:</strong> Apollo already tows {apolloCount} of their {properties.length} — lead with the properties we serve, then talk about the rest.
        </p>
      ) : null}

      <h2 className="fwaa-section-title">Notes</h2>
      <p className="fwaa-section-sub">Visible to the whole team — log the conversation and next steps.</p>
      <div className="fwaa-appt fwaa-appt--noteshell">
        <NotesThread attendeeId={attendee.id} notes={data.notes} myUserId={user?.id} onChanged={load} />
      </div>

      <h2 className="fwaa-section-title">Portfolio</h2>
      <FwaaPropertyTable properties={properties} highlightAttendeeId={attendee.id} />
    </div>
  );
}

function HeadKpi({ value, label, accent }) {
  return (
    <div className={`fwaa-kpi${accent ? ` fwaa-kpi--${accent}` : ''}`}>
      <div className="fwaa-kpi__value">{value}</div>
      <div className="fwaa-kpi__label">{label}</div>
    </div>
  );
}
